import { Button, Form, Modal, Table, Typography } from "antd";
import { type Dispatch, type HTMLAttributes, type SetStateAction, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { AddPersonModal } from "~components/AddPersonModal";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { ImageTypes } from "~enums/imageTypes";
import { RoleStatuses } from "~enums/roleStatuses";
import type { Roles } from "~enums/roles";
import { useAddRole } from "~hooks/apiHooks/useAddRole";
import { useFetchPersons } from "~hooks/apiHooks/useFetchPersons";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { findImageByType } from "~utilities/findImageByType";
import { onTableChange } from "~utilities/onTableChange";
import classNames from "~utils/classNames";
import styles from "./AddPersonRolesModal.module.css";
import { AddPersonRolesModalFilters } from "./components/AddPersonRolesModalFilters";
import { useAddPersonRolesModalColumns } from "./hooks/useAddPersonRolesModalColumns";
import type { AddOfficialsTableRow } from "./types/addOfficialsTableRow";

type RowSubmitData = {
  role: Roles;
  status: RoleStatuses;
};

type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  addRole: ({ role, status }: RowSubmitData) => Promise<void>;
};

const TableRow = ({ addRole, ...props }: TableRowProps) => {
  const [form] = Form.useForm<RowSubmitData>();
  return (
    <Form form={form} onFinish={addRole} initialValues={{ status: RoleStatuses.Active }} component={false}>
      <tr {...props} />
    </Form>
  );
};

type TableCellProps = HTMLAttributes<HTMLTableCellElement> & {
  isEditing: boolean;
  record: AddOfficialsTableRow;
};

const TableCell = ({ isEditing, record, ...props }: TableCellProps) => (
  <td {...props} className={classNames(props.className, isEditing ? styles.editCell : styles.cell)} />
);

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  roles: Roles[];
  statuses: RoleStatuses[];
  refetch?: () => void;
};

const DEFAULT_SORT_FIELD = "nameFullLocal";

export const AddPersonRolesModal = ({ open, setOpen, roles, statuses, refetch: extRefetch }: Props) => {
  const { formatMessage } = useIntl();

  const { seasonId = "" } = useParams();

  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState(false);

  const [searchValue, setSearchValue] = useState("");

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions, resetPagination } =
    useHandlePagination();

  const [nationality, setNationality] = useState<string | undefined>();
  const [gender, setGender] = useState<string | undefined>();
  const [status, setStatus] = useState<RoleStatuses | undefined>();
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_FIELD);

  const params: Record<string, string> = {
    limit: pageSize.toString(),
    sortBy,
    offset: offset.toString(),
    ...(searchValue.length > 1 && { nameFullLocalContains: searchValue }),
    ...(nationality && { nationality }),
    ...(gender && { gender }),
    ...(status && { status }),
  };

  const {
    data: dataSource,
    isFetching,
    refetch: refetchPersons,
  } = useFetchPersons(
    params,
    (data) =>
      data.data.map((person) => ({
        nameFullLocal: person.nameFullLocal,
        personId: person.personId,
        avatarUrl: findImageByType(person?.images ?? [], ImageTypes.PersonHead) ?? "",
        nationality: person.nationality,
        gender: person.gender,
      })),
    {
      enabled: open,
    },
  );

  const { mutateAsync: addRole, isPending } = useAddRole();

  const isLoading = isFetching || isPending;

  const columns = useAddPersonRolesModalColumns(roles, statuses, isLoading);

  const { hasPermission } = useUserPermissions();
  const hasAddPersonPermission = hasPermission(PermissionNames.PERSON, PermissionOperations.CREATE);

  const onChange = onTableChange<AddOfficialsTableRow>(setSortBy, DEFAULT_SORT_FIELD);

  const onCreateCb = async (localFullName: string) => {
    await refetchPersons();
    resetPagination();
    setSearchValue(localFullName);
  };

  return (
    <>
      {hasAddPersonPermission && (
        <AddPersonModal open={isAddPersonModalOpen} setOpen={setIsAddPersonModalOpen} onCreateCb={onCreateCb} />
      )}
      <Modal
        open={open}
        title={formatMessage({ id: "person.role.add" })}
        cancelText={formatMessage({ id: "cancel" })}
        onCancel={() => {
          setSearchValue("");
          setOpen(false);
        }}
        okButtonProps={{ hidden: true }}
        destroyOnClose
        width={1000}
      >
        <div className={styles.topBar}>
          <div className={styles.topLeftContainer}>
            <Typography.Title level={4} className={styles.title}>
              {formatMessage({ id: "persons" })}
            </Typography.Title>
          </div>
          <div className={styles.topRightContainer}>
            <AddPersonRolesModalFilters
              setGender={setGender}
              setNationality={setNationality}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setStatus={setStatus}
              disabled={isLoading}
              resetPagination={resetPagination}
            />
            {hasAddPersonPermission && (
              <Button
                onClick={() => setIsAddPersonModalOpen(true)}
                type="primary"
                icon={<i className="fas fa-plus" />}
                disabled={isLoading}
              >
                {formatMessage({ id: "person" })}
              </Button>
            )}
          </div>
        </div>
        <Table
          size="small"
          dataSource={dataSource}
          rowKey="personId"
          loading={isLoading}
          columns={columns}
          className={styles.tableContainer}
          onChange={onChange}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: pageSizeOptions,
            showQuickJumper: false,
            total: offset + (dataSource?.length ?? 0) + 1,
            position: ["bottomCenter"],
            onChange: handlePaginationChange,
            showTotal: () => formatMessage({ id: "pageN" }, { number: currentPage }),
          }}
          onRow={(record) =>
            ({
              addRole: ({ role, status }: RowSubmitData) =>
                addRole({ personId: record.personId, role, status, seasonId }).then(() => {
                  extRefetch?.();
                  refetchPersons();
                }),
            }) as HTMLAttributes<HTMLTableRowElement>
          }
          components={{
            body: {
              cell: TableCell,
              row: TableRow,
            },
          }}
        />
      </Modal>
    </>
  );
};
